import React, { Component } from 'react';
import { translate } from 'react-i18next';
import { Link } from 'react-router';
import Button from 'antd/lib/button';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import Select from 'antd/lib/select';
import api from '../api';

const TEMPLATE_COPY = {
  borrower: {
    label: 'Dlžník bez pôžičky',
    status: 'Overený účet bez predchádzajúcej pôžičky',
    detail: 'Účet je pripravený na prvú žiadosť o pôžičku.',
  },
  repeat: {
    label: 'Opakovaný dlžník',
    status: 'Overený účet s jednou úplne splatenou pôžičkou',
    detail: 'Účet obsahuje splatenú pôžičku a históriu splátok. Možno podať novú žiadosť.',
  },
  up1: {
    label: 'Dlžník – 1. upomienka',
    status: 'Pôžička po splatnosti · UP1',
    detail: 'Kalendár nesplatených splátok zodpovedá prvej upomienke.',
  },
  up2: {
    label: 'Dlžník – 2. upomienka',
    status: 'Pôžička po splatnosti · UP2',
    detail: 'Kalendár nesplatených splátok zodpovedá druhej upomienke.',
  },
  odp: {
    label: 'Dlžník – odpredaj',
    status: 'Pôžička po splatnosti · ODP',
    detail: 'Kalendár nesplatených splátok zodpovedá odkupu pohľadávky.',
  },
  investor: {
    label: 'Investor',
    status: 'Overený účet, zostatok 0 €',
    detail: 'Účet je pripravený na používanie. Prostriedky pridáte cez nástroj na navýšenie zostatku.',
  },
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatEuro = value => new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR' }).format(Number(value) || 0);

const errorMessage = (res, fallback) => (
  res && res.response && res.response.data && typeof res.response.data.error === 'string'
    ? res.response.data.error
    : fallback
);

export class CreateAccountTool extends Component {
  constructor(props) {
    super(props);

    this.state = {
      metadata: null,
      metadataLoading: true,
      metadataError: null,
      template: 'borrower',
      email: '',
      password: '',
      amount: null,
      termMonths: null,
      creating: false,
      error: null,
      result: null,
      copyStatus: null,
    };
  }

  componentDidMount() {
    this.mounted = true;
    this.loadTemplates();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  loadTemplates = async () => {
    this.setState({ metadataLoading: true, metadataError: null });
    const res = await api.listAccountTemplates();
    if (!this.mounted) {
      return;
    }
    if (res && res.ok && res.data && Array.isArray(res.data.templates)) {
      const { loan } = res.data;
      this.setState({
        metadata: res.data,
        metadataLoading: false,
        amount: loan ? loan.amountDefault : null,
        termMonths: loan ? loan.termMonthsDefault : null,
      });
    } else {
      this.setState({
        metadataLoading: false,
        metadataError: errorMessage(res, this.props.t('Šablóny účtov sa nepodarilo načítať.')),
      });
    }
  };

  selectedTemplate() {
    const { metadata, template } = this.state;
    return metadata.templates.filter(item => item.id === template)[0];
  }

  validate() {
    const { t } = this.props;
    const { metadata, email, password, amount, termMonths } = this.state;
    const trimmedEmail = email.trim();

    if (trimmedEmail && !EMAIL_PATTERN.test(trimmedEmail)) {
      return t('Zadajte platný e-mail alebo nechajte pole prázdne, aby sa vygeneroval automaticky.');
    }
    if (password && password.length < metadata.passwordMinLength) {
      return t('Vlastné heslo musí mať aspoň {{count}} znakov. Predvolené heslo použijete ponechaním prázdneho poľa.', { count: metadata.passwordMinLength });
    }
    if (this.selectedTemplate().includesLoan) {
      const { loan } = metadata;
      if (!loan) {
        return t('Šablóny s pôžičkou nie sú dostupné: nastavenia kalkulačky v tomto prostredí sú neúplné.');
      }
      const amountValid = Number.isInteger(amount) && amount >= loan.amountMin && amount <= loan.amountMax
        && (amount - loan.amountMin) % loan.amountStep === 0;
      const termValid = Number.isInteger(termMonths) && termMonths >= loan.termMonthsMin && termMonths <= loan.termMonthsMax;
      if (!amountValid || !termValid) {
        return t('Zadajte sumu od {{amountMin}} € do {{amountMax}} € s krokom {{amountStep}} € a splatnosť od {{termMonthsMin}} do {{termMonthsMax}} mes.', loan);
      }
    }
    return null;
  }

  handleSubmit = async (e) => {
    e.preventDefault();
    const { t } = this.props;
    const { template, email, password, amount, termMonths, creating } = this.state;
    if (creating) {
      return;
    }

    const validationError = this.validate();
    if (validationError) {
      this.setState({ error: validationError });
      return;
    }

    const body = { template };
    if (email.trim()) {
      body.email = email.trim();
    }
    if (password) {
      body.password = password;
    }
    if (this.selectedTemplate().includesLoan) {
      body.amount = amount;
      body.termMonths = termMonths;
    }

    this.setState({ creating: true, error: null });
    const res = await api.createAccount(body);
    if (!this.mounted) {
      return;
    }
    if (res && res.ok && res.data && res.data.accountId) {
      this.setState({ creating: false, result: res.data, copyStatus: null });
    } else {
      this.setState({
        creating: false,
        error: errorMessage(res, t('Nepodarilo sa potvrdiť vytvorenie účtu. Účet už môže byť uložený. Pred opakovaním skontrolujte zoznam používateľov.')),
      });
    }
  };

  handleTemplateChange = (template) => {
    this.setState({ template, error: null });
  };

  handleCreateAnother = () => {
    this.setState({ result: null, email: '', password: '', error: null, copyStatus: null });
  };

  copy = (value, copiedLabel) => {
    const { t } = this.props;
    const manual = t('Označte hodnotu a skopírujte ju manuálne.');
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value)
        .then(() => this.mounted && this.setState({ copyStatus: copiedLabel }))
        .catch(() => this.mounted && this.setState({ copyStatus: manual }));
    } else {
      this.setState({ copyStatus: manual });
    }
  };

  renderPreview(templateCopy, includesLoan) {
    const { t } = this.props;
    const { metadata, template, email, password, amount, termMonths } = this.state;
    const overdueDays = metadata.overdueDays && (template === 'up1' || template === 'up2' || template === 'odp')
      ? metadata.overdueDays[template]
      : null;

    let loanLine = null;
    if (includesLoan && metadata.loan) {
      const kind = template === 'repeat'
        ? t('predchádzajúca, úplne splatená pôžička')
        : overdueDays !== null
          ? t('pôžička po splatnosti · počet dní: {{count}}', { count: overdueDays })
          : t('pôžička po splatnosti');
      loanLine = `${formatEuro(amount)} · ${t('{{count}} mes.', { count: termMonths || 0 })} · ${kind}`;
    }

    return (
      <div className="ca-preview" data-testid="create-account-preview" aria-live="polite">
        <span className="ca-label">{t('Vytvorí sa')}</span>
        <strong>{t(templateCopy.status)}</strong>
        <p>{t(templateCopy.detail)}</p>
        {loanLine && <p data-testid="create-account-preview-loan">{loanLine}</p>}
        <p>
          {email.trim()
            ? t('E-mail: {{email}}', { email: email.trim().toLowerCase(), interpolation: { escapeValue: false } })
            : t('E-mail: vygeneruje sa automaticky')}
        </p>
        <p className="ca-note">
          {password ? t('Vygenerovaný testovací profil · vlastné heslo · žiadne skutočné správy') : t('Vygenerovaný testovací profil · predvolené testovacie heslo · žiadne skutočné správy')}
        </p>
      </div>
    );
  }

  renderForm() {
    const { t } = this.props;
    const { metadata, template, email, password, amount, termMonths, creating, error } = this.state;
    const selected = this.selectedTemplate();
    const templateCopy = TEMPLATE_COPY[template];

    return (
      <form onSubmit={this.handleSubmit} noValidate data-testid="create-account-form">
        <p className="ca-intro">{t('Vytvorte testovací účet s pripraveným profilom.')}</p>

        <label className="ca-label" htmlFor="create-account-template">{t('Šablóna účtu')}</label>
        <Select
          id="create-account-template"
          className="ca-select"
          getPopupContainer={trigger => trigger.parentNode}
          value={template}
          onChange={this.handleTemplateChange}
          disabled={creating}
          data-testid="create-account-template"
        >
          {metadata.templates.map(item => (
            <Select.Option key={item.id} value={item.id}>{t(TEMPLATE_COPY[item.id].label)}</Select.Option>
          ))}
        </Select>

        <div className="ca-credentials" data-testid="create-account-credentials">
          <label className="ca-label" htmlFor="create-account-email">
            {t('E-mail')} <span className="ca-optional">({t('nepovinné')})</span>
          </label>
          <Input
            id="create-account-email"
            type="email"
            autoComplete="off"
            value={email}
            placeholder={t('Automaticky vygenerovať jedinečný e-mail')}
            onChange={e => this.setState({ email: e.target.value, error: null })}
            disabled={creating}
            aria-describedby="create-account-credentials-help"
            data-testid="create-account-custom-email"
          />
          <label className="ca-label ca-password-label" htmlFor="create-account-password">
            {t('Heslo')} <span className="ca-optional">({t('nepovinné')})</span>
          </label>
          <Input
            id="create-account-password"
            type="password"
            autoComplete="new-password"
            value={password}
            placeholder={t('Použiť predvolené testovacie heslo')}
            onChange={e => this.setState({ password: e.target.value, error: null })}
            disabled={creating}
            aria-describedby="create-account-credentials-help"
            data-testid="create-account-custom-password"
          />
          <p id="create-account-credentials-help" className="ca-note">
            {t('E-mail aj heslo môžete zadať samostatne. Prázdne polia použijú predvolené hodnoty. Vlastné heslo musí mať aspoň {{count}} znakov. Skutočné e-maily ani SMS sa neodošlú.', { count: metadata.passwordMinLength })}
          </p>
        </div>

        {selected.includesLoan && metadata.loan && (
          <div className="ca-fields" data-testid="create-account-loan-fields">
            <div>
              <label className="ca-label" htmlFor="create-account-amount">{t('Výška pôžičky (€)')}</label>
              <InputNumber
                id="create-account-amount"
                min={metadata.loan.amountMin}
                max={metadata.loan.amountMax}
                step={metadata.loan.amountStep}
                value={amount}
                onChange={value => this.setState({ amount: value, error: null })}
                disabled={creating}
                data-testid="create-account-amount"
              />
            </div>
            <div>
              <label className="ca-label" htmlFor="create-account-term">{t('Splatnosť (mesiace)')}</label>
              <InputNumber
                id="create-account-term"
                min={metadata.loan.termMonthsMin}
                max={metadata.loan.termMonthsMax}
                value={termMonths}
                onChange={value => this.setState({ termMonths: value, error: null })}
                disabled={creating}
                data-testid="create-account-term"
              />
            </div>
          </div>
        )}
        {selected.includesLoan && metadata.loan && (
          <p className="ca-note" data-testid="create-account-loan-limits">
            {t('V tomto prostredí: {{amountMin}}–{{amountMax}} € s krokom {{amountStep}} €, {{termMonthsMin}}–{{termMonthsMax}} mes.', metadata.loan)}
          </p>
        )}
        {selected.includesLoan && !metadata.loan && (
          <p className="ca-note" data-testid="create-account-loan-unavailable">
            {t('Šablóny s pôžičkou nie sú dostupné: nastavenia kalkulačky v tomto prostredí sú neúplné.')}
          </p>
        )}

        {this.renderPreview(templateCopy, selected.includesLoan)}

        {error && <p role="alert" className="ca-error" data-testid="create-account-error">{error}</p>}

        <Button
          type="primary"
          htmlType="submit"
          className="ca-primary"
          loading={creating}
          disabled={creating}
          aria-busy={creating}
          data-testid="create-account-submit"
        >
          {creating ? t('Vytvára sa testovací účet…') : t('Vytvoriť testovací účet')}
        </Button>
        {creating && (
          <span role="status" className="ca-note" data-testid="create-account-loading">
            {t('Pripravuje sa profil a údaje scenára…')}
          </span>
        )}
      </form>
    );
  }

  renderResult() {
    const { t, onClose } = this.props;
    const { result, copyStatus } = this.state;
    const templateCopy = TEMPLATE_COPY[result.template];

    return (
      <section className="ca-result" data-testid="create-account-success" aria-label={t('Vytvorený účet')}>
        <p role="status" className="ca-success">{t('Testovací účet bol vytvorený')}</p>
        <p>{result.name} · {t(templateCopy.label)}</p>
        <p className="ca-note">{t(templateCopy.status)}</p>

        <label className="ca-label" htmlFor="create-account-created-email">{t('E-mail')}</label>
        <div className="ca-copy">
          <Input id="create-account-created-email" value={result.email} readOnly data-testid="create-account-email" />
          <Button onClick={() => this.copy(result.email, t('E-mail skopírovaný'))} data-testid="create-account-copy-email">
            {t('Kopírovať e-mail')}
          </Button>
        </div>

        <label className="ca-label" htmlFor="create-account-created-password">{t('Heslo')}</label>
        <div className="ca-copy">
          <Input
            id="create-account-created-password"
            type="password"
            value={result.password}
            readOnly
            data-testid="create-account-password"
          />
          <Button onClick={() => this.copy(result.password, t('Heslo skopírované'))} data-testid="create-account-copy-password">
            {t('Kopírovať heslo')}
          </Button>
        </div>
        <p role="status" className="ca-note" data-testid="create-account-copy-status">{copyStatus}</p>

        <div className="ca-actions">
          <Link
            to={`/admin/accounts/${result.accountId}/profile`}
            className="ant-btn ant-btn-primary ca-link-button"
            onClick={onClose}
            data-testid="create-account-open-profile"
          >
            {t('Otvoriť účet')}
          </Link>
          <Button onClick={this.handleCreateAnother} data-testid="create-account-another">
            {t('Vytvoriť ďalší')}
          </Button>
        </div>
        <p className="ca-note">{t('Zostávate prihlásený ako administrátor. Možnosť „Prihlásiť ako“ nájdete v profile účtu.')}</p>
      </section>
    );
  }

  render() {
    const { t } = this.props;
    const { metadata, metadataLoading, metadataError, result } = this.state;

    let content;
    if (metadataLoading) {
      content = <p role="status" className="ca-note" data-testid="create-account-metadata-loading">{t('Načítavajú sa šablóny účtov…')}</p>;
    } else if (metadataError) {
      content = (
        <div data-testid="create-account-metadata-error">
          <p role="alert" className="ca-error">{metadataError}</p>
          <Button onClick={this.loadTemplates} data-testid="create-account-metadata-retry">{t('Skúsiť znova')}</Button>
        </div>
      );
    } else if (result) {
      content = this.renderResult();
    } else if (metadata) {
      content = this.renderForm();
    }

    return (
      <section
        className="ca-tool"
        data-testid="create-account-tool"
        aria-label={t('Vytvoriť testovací účet')}
        onMouseDown={e => e.stopPropagation()}
      >
        {content}
      </section>
    );
  }
}

export default translate()(CreateAccountTool);



// WEBPACK FOOTER //
// ./src/features/test-tools/tools/CreateAccountTool.js